import * as React from "react";
import Select from 'react-select';
import {none, option, Option} from "ts-option";
import {PlaceholderProps} from "../../../../../common/props/PlaceholderProps";
import {SelectComponentDefaultValue} from "../../../../../ui/select/SelectComponent";
import {ActionMeta, InputActionMeta} from "react-select/lib/types";
import {api} from "../../../../../../api/api";
import {notifyBugsnagAndShowToast} from "../../../../../../utils/Utils";
import CurrencyHelper from "../../../../../../helper/CurrencyHelper";
import BillSummary from "../../../../../../model/bill/BillSummary";
import classNames = require("classnames");

interface ComponentProps {
	/**
	 * Callback on select bill.
	 * @param {Option<BillSummary>} bill.
	 */
	onSelect: (bill: BillSummary) => void;
	/**
	 * Initial search query text.
	 */
	initialSearchQuery?: Option<string>
}

type Props = ComponentProps & PlaceholderProps;

interface State {
	/**
	 * Text typed by user.
	 */
	queryText: string;
	/**
	 * Bills available for user selection.
	 */
	bills: BillSummary[];
	/**
	 * Indicates if data is loading.
	 */
	loading: boolean;
	/**
	 * Last request query.
	 */
	lastRequestedQuery: Option<string>;
}

const minCharsToSearch = 2;

export default class BillSelectionComponent extends React.PureComponent<Props, State> {
	public static defaultProps: Partial<Props> = {
		initialSearchQuery: none
	};

	constructor(props: Props) {
		super(props);
		this.onInputChange = this.onInputChange.bind(this);
		this.onSelected = this.onSelected.bind(this);
		this.getNoResultsText = this.getNoResultsText.bind(this);
		const initialQueryText = this.props.initialSearchQuery ? this.props.initialSearchQuery.getOrElse("") : "";
		this.state = {
			queryText: initialQueryText,
			bills: [],
			loading: false,
			lastRequestedQuery: none
		};
	}

	render() {
		const value = this.state.queryText.length ?
			new SelectComponentDefaultValue(this.state.queryText, this.state.queryText) : null;
		return (
			<Select
				classNamePrefix={"c-select-component"}
				inputValue={this.state.queryText}
				value={value}
				styles={{menuList: (base) => ({...base, maxHeight: "186px"})}}
				placeholder={this.props.placeholder}
				noOptionsMessage={this.getNoResultsText}
				filterOption={(element: any, filter: string) => true}
				components={{Option: this.renderOptionComponent}}
				options={this.state.bills}
				isLoading={this.state.loading}
				onInputChange={this.onInputChange}
				onChange={this.onSelected}
				closeMenuOnSelect={false}
				openMenuOnFocus={true}/>
		)
	}

	/**
	 * Render option component (select dropdown list element)
	 * @param props properties for list element.
	 */
	renderOptionComponent(props: any): JSX.Element {
		const {innerProps, innerRef, isFocused, className} = props;
		const classFromProps = classNames(
			{
				"c-reconciliation-modal_select-option-component": true,
				"has-focus": isFocused
			},
			className
		);

		const data = props.data as BillSummary;

		return (
			<div ref={innerRef} className={classFromProps} {...innerProps}>
				<div className="c-reconciliation-modal_select-option-component_upper-container">
					<div className="c-reconciliation-modal_select-option-component_upper-container_labels">
						<div>
							<b>{data.referenceNumber}</b>
							<span>{data.partnerName}</span>
						</div>
					</div>
				</div>
				<div className="c-reconciliation-modal_select-option-component_border"/>
				<div className="c-reconciliation-modal_select-option-component_lower-container">
					<div>
						<span>Amount due</span>
						<span>Total</span>
					</div>
					<div className="c-reconciliation-modal_select-option-component_lower-container_values-container">
						<span>{CurrencyHelper.toCurrency(data.due)}</span>
						<span>{CurrencyHelper.toCurrency(data.total)}</span>
					</div>
				</div>
			</div>
		)
	}

	getNoResultsText(): string {
		if (this.state.loading)
			return "Loading ...";
		else if (this.state.queryText.length >= minCharsToSearch)
			return "No results found";
		else
			return "Type to search";
	}

	/**
	 * On changed value in select, fetch data from store or fetch from server.
	 * @param searchText text in the input.
	 * @param {InputActionMeta} inputActionMeta
	 */
	onInputChange(searchText: string, inputActionMeta: InputActionMeta) {
		if (inputActionMeta.action === 'input-change') {
			this.setState({queryText: searchText});
			if (searchText.length >= minCharsToSearch) {
				this.findBySearchText(searchText);
			}
			else {
				this.setState({bills: [], loading: false});
			}
		}
	}

	/**
	 * Callback on selected item.
	 * @param {BillSummary} selected selected item.
	 * @param {ActionMeta} actionMeta performed action data.
	 */
	onSelected(selected: BillSummary, actionMeta: ActionMeta) {
		if (actionMeta.action === 'clear') {
			this.setState({queryText: "", bills: []});
		}
		else if (!(selected instanceof Array)) {
			this.props.onSelect(selected);
		}
	}

	/**
	 * Fetch bills by search text.
	 * @param {string} searchText text to search by.
	 */
	private findBySearchText(searchText: string) {
		this.setState({loading: true, lastRequestedQuery: option(searchText)}, () => {
			api.instance.billApi.findBillsByText(searchText).then(result => {
				this.setState({bills: result, loading: false});
			}).catch((error) => {
				this.setState({bills: [], loading: false});
				notifyBugsnagAndShowToast(error, "Error on searching bills");
			});
		});
	}

	/**
	 * Reset search input text.
	 */
	public resetSearchInput() {
		this.setState({queryText: ""});
	}
}
